import { useEffect, useState } from 'react';
import { Coins } from 'lucide-react';
import { Button } from '@/components/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/base/card';
import { Input } from '@/components/base/input';
import { Label } from '@/components/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/base/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/base/alert';
import { explorerTxUrl, sendSplTransfer } from '@/solana';
import { useAccount } from '@/state/account';

function shortMint(m: string) {
  return `${m.slice(0, 4)}…${m.slice(-4)}`;
}

export function SendSplCard() {
  const { account, network, connection, holdings, holdingsError, pushTx, refreshBalances } = useAccount();
  const [mint, setMint] = useState('');
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Drop the selected mint if it disappears from holdings (e.g. after refresh).
  useEffect(() => {
    if (mint && !holdings.some(h => h.mint === mint)) setMint('');
  }, [holdings, mint]);

  if (!account) return null;

  const selected = holdings.find(h => h.mint === mint) ?? null;
  const valid =
    !!selected && recipient.trim().length > 0 && parseFloat(amount) > 0 && Number.isFinite(parseFloat(amount));

  const submit = async () => {
    if (!valid || !selected) return;
    setBusy(true);
    setError(null);
    setSignature(null);
    try {
      const sig = await sendSplTransfer(
        connection,
        account.scalar,
        account.pubkey,
        selected,
        recipient.trim(),
        parseFloat(amount),
      );
      setSignature(sig);
      pushTx({ kind: 'spl', signature: sig, network });
      setAmount('');
      void refreshBalances();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to send token.');
    } finally {
      setBusy(false);
    }
  };

  const placeholder = holdingsError
    ? 'Token list unavailable on this RPC'
    : holdings.length === 0
      ? 'No tokens detected — refresh balances first'
      : 'Select a token…';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="para:flex para:items-center para:gap-2">
          <Coins className="para:size-5" />
          Send SPL token
        </CardTitle>
      </CardHeader>
      <CardContent className="para:flex para:flex-col para:gap-3">
        <div className="para:flex para:flex-col para:gap-2">
          <Label htmlFor="spl-mint">Token</Label>
          <Select value={mint} onValueChange={setMint} disabled={holdings.length === 0}>
            <SelectTrigger id="spl-mint">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {holdings.map((h) => (
                <SelectItem key={h.mint + h.programId} value={h.mint}>
                  {shortMint(h.mint)}
                  {' — '}
                  {h.uiAmount} available
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="para:flex para:flex-col para:gap-2">
          <Label htmlFor="spl-recipient">Recipient address</Label>
          <Input
            id="spl-recipient"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="Solana address"
            spellCheck={false}
          />
        </div>
        <div className="para:flex para:flex-col para:gap-2">
          <Label htmlFor="spl-amount">Amount</Label>
          <Input
            id="spl-amount"
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Transfer failed</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {signature && (
          <Alert>
            <AlertTitle>Transaction submitted</AlertTitle>
            <AlertDescription>
              <a
                href={explorerTxUrl(signature, network)}
                target="_blank"
                rel="noreferrer"
                className="para:underline para:break-all"
              >
                {signature}
              </a>
            </AlertDescription>
          </Alert>
        )}
        <Button onClick={submit} disabled={!valid || busy} isLoading={busy}>
          Send token
        </Button>
      </CardContent>
    </Card>
  );
}
